/**
 * Language Synchronization Utility
 * 
 * Syncs language changes between front_compliance and new_demo_frontend (DCC)
 * Uses cookies for persistence and postMessage for cross-window communication
 */

const LANGUAGE_COOKIE_NAME = 'shared_app_language';
const LANGUAGE_SYNC_MESSAGE = 'LANGUAGE_CHANGED';

/**
 * Get the domain for cookies (works for localhost)
 */
function getCookieDomain(): string {
  const hostname = window.location.hostname;
  // For localhost, we can't set domain, so return empty string
  // For production, you might want to set a specific domain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  // Extract base domain (e.g., 'pianat.ai' from 'demo.pianat.ai')
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return '';
}

/**
 * Set language in cookie (shared between apps)
 */
export function setLanguageCookie(language: string): void {
  try {
    const domain = getCookieDomain();
    const cookieString = `${LANGUAGE_COOKIE_NAME}=${language}; path=/; max-age=31536000; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
    document.cookie = cookieString;
  } catch (error) {
    console.warn('Failed to set language cookie:', error);
  }
}

/**
 * Get language from cookie
 */
export function getLanguageCookie(): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(^| )${LANGUAGE_COOKIE_NAME}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (error) {
    console.warn('Failed to get language cookie:', error);
    return null;
  }
}

/**
 * Broadcast language change to other windows/tabs
 */
export function broadcastLanguageChange(language: string): void {
  try {
    // Use BroadcastChannel for same-origin communication
    const channel = new BroadcastChannel('language-sync');
    channel.postMessage({ type: LANGUAGE_SYNC_MESSAGE, language });
    channel.close();
  } catch (error) {
    console.warn('BroadcastChannel not supported, using postMessage:', error);
  }

  // Also use postMessage for cross-origin communication (if opened in different windows)
  try {
    // Send to all possible parent/opener windows
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: LANGUAGE_SYNC_MESSAGE, language }, '*');
    }
    // Send to all child windows
    if ((window as any).childWindows) {
      (window as any).childWindows.forEach((child: Window) => {
        if (!child.closed) {
          child.postMessage({ type: LANGUAGE_SYNC_MESSAGE, language }, '*');
        }
      });
    }
  } catch (error) {
    console.warn('Failed to send postMessage:', error);
  }

  // Store in localStorage as well (for same-origin tabs)
  try {
    localStorage.setItem(LANGUAGE_COOKIE_NAME, language);
    localStorage.setItem('selectedLanguage', language); // For backward compatibility
  } catch (error) {
    console.warn('Failed to set language in localStorage:', error);
  }
}

/**
 * Listen for language changes from other windows/tabs
 */
export function listenForLanguageChanges(
  callback: (language: string) => void
): () => void {
  const cleanupFunctions: (() => void)[] = [];

  // Listen to BroadcastChannel
  try {
    const channel = new BroadcastChannel('language-sync');
    const handler = (event: MessageEvent) => {
      if (event.data?.type === LANGUAGE_SYNC_MESSAGE) {
        callback(event.data.language);
      }
    };
    channel.addEventListener('message', handler);
    cleanupFunctions.push(() => {
      channel.removeEventListener('message', handler);
      channel.close();
    });
  } catch (error) {
    console.warn('BroadcastChannel not supported:', error);
  }

  // Listen to postMessage
  const postMessageHandler = (event: MessageEvent) => {
    // Accept messages from any origin (for development)
    // In production, you might want to validate event.origin
    if (event.data?.type === LANGUAGE_SYNC_MESSAGE) {
      callback(event.data.language);
    }
  };
  window.addEventListener('message', postMessageHandler);
  cleanupFunctions.push(() => {
    window.removeEventListener('message', postMessageHandler);
  });

  // Listen to storage events (for same-origin tabs)
  const storageHandler = (event: StorageEvent) => {
    if (event.key === LANGUAGE_COOKIE_NAME && event.newValue) {
      callback(event.newValue);
    }
  };
  window.addEventListener('storage', storageHandler);
  cleanupFunctions.push(() => {
    window.removeEventListener('storage', storageHandler);
  });

  // Poll cookie changes (fallback for cross-origin scenarios)
  let lastLanguage = getLanguageCookie();
  const pollInterval = setInterval(() => {
    const currentLanguage = getLanguageCookie();
    if (currentLanguage && currentLanguage !== lastLanguage) {
      lastLanguage = currentLanguage;
      callback(currentLanguage);
    }
  }, 1000); // Check every second
  cleanupFunctions.push(() => {
    clearInterval(pollInterval);
  });

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
}

/**
 * Initialize language sync - get language from cookie or localStorage
 * Priority: 1. Shared cookie (for cross-app sync), 2. localStorage (for backward compatibility)
 */
export function initializeLanguageSync(): string | null {
  // Try shared cookie first (works across different ports/origins)
  const cookieLanguage = getLanguageCookie();
  if (cookieLanguage) {
    // Also sync to localStorage for consistency
    try {
      localStorage.setItem(LANGUAGE_COOKIE_NAME, cookieLanguage);
      localStorage.setItem('selectedLanguage', cookieLanguage); // For backward compatibility
    } catch (error) {
      console.warn('Failed to sync language to localStorage:', error);
    }
    return cookieLanguage;
  }

  // Fallback to localStorage (for backward compatibility and same-origin)
  try {
    // Check both keys for compatibility
    const storedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME) || localStorage.getItem('selectedLanguage');
    if (storedLanguage) {
      // Sync to cookie so both apps can access it
      setLanguageCookie(storedLanguage);
      // Ensure both localStorage keys are set
      localStorage.setItem(LANGUAGE_COOKIE_NAME, storedLanguage);
      localStorage.setItem('selectedLanguage', storedLanguage);
      return storedLanguage;
    }
  } catch (error) {
    console.warn('Failed to get language from localStorage:', error);
  }

  return null;
}

